import React, { useState } from 'react';
import {
    Calculator as CalcIcon,
    Info,
    Copy,
    Check,
    Sparkles,
    Tag,
    TrendingUp,
    ShieldCheck,
    Layers,
    RotateCcw
} from 'lucide-react';
import toast from 'react-hot-toast';

interface CategoryOption {
    label: string;
    group: string;
    gstRate: number; // e.g. 0.05 for 5%
    hsn?: string;
}

const CATEGORY_OPTIONS: CategoryOption[] = [
    // Grocery & Pantry
    { label: 'Grocery – Branded Staples & Packaged Papad (5%)', group: 'Grocery & Pantry', gstRate: 0.05, hsn: '1906/1905' },
    { label: 'Grocery – Masala & Seasoning / Spices (5%)', group: 'Grocery & Pantry', gstRate: 0.05, hsn: '0910/2103' },
    { label: 'Grocery – Pickles, Chutney, Jam, Sauce (5%)', group: 'Grocery & Pantry', gstRate: 0.05, hsn: '2001/2007' },
    { label: 'Grocery – Namkeen, Bhujia, Snacks, Chips (5%)', group: 'Grocery & Pantry', gstRate: 0.05, hsn: '2106/1905' },
    { label: 'Grocery – Indian Sweets, Bakery, Biscuits (5%)', group: 'Grocery & Pantry', gstRate: 0.05, hsn: '1905/1806' },
    { label: 'Grocery – Ghee, Butter, Dairy Fats (5%)', group: 'Grocery & Pantry', gstRate: 0.05, hsn: '0405' },
    { label: 'Grocery – Unbranded Staples (Rice, Dal, Atta) (0%)', group: 'Grocery & Pantry', gstRate: 0.0, hsn: '1006/1101' },
    { label: 'Grocery – Raw Honey (Unbranded) (0%)', group: 'Grocery & Pantry', gstRate: 0.0, hsn: '0409' },
    { label: 'Grocery – Branded Honey / Packaged Honey (5%)', group: 'Grocery & Pantry', gstRate: 0.05, hsn: '0409' },
    { label: 'Grocery – Edible Oils (Mustard, Groundnut, Coconut) (5%)', group: 'Grocery & Pantry', gstRate: 0.05, hsn: '1509/1515' },
    { label: 'Grocery – Dry Fruits & Nuts (5%)', group: 'Grocery & Pantry', gstRate: 0.05, hsn: '0801/0802' },
    { label: 'Grocery – Jaggery, Sugar, Salt (5%)', group: 'Grocery & Pantry', gstRate: 0.05, hsn: '1701/1703' },

    // Fashion & Apparel
    { label: 'Fashion – Sarees (All types & handloom) (5%)', group: 'Fashion & Apparel', gstRate: 0.05, hsn: '5007/5208' },
    { label: 'Fashion – Garments & Apparel ≤ ₹2,500/pc (5%)', group: 'Fashion & Apparel', gstRate: 0.05, hsn: '6109/6204' },
    { label: 'Fashion – Garments & Apparel > ₹2,500/pc (18%)', group: 'Fashion & Apparel', gstRate: 0.18, hsn: '6109/6204' },
    { label: 'Fashion – Shawls, Stoles, Dupattas (5%)', group: 'Fashion & Apparel', gstRate: 0.05, hsn: '6214' },
    { label: 'Fashion – Handbags, Totes, Jute Bags, Pouches (5%)', group: 'Fashion & Apparel', gstRate: 0.05, hsn: '4202/6305' },
    { label: 'Fashion – Footwear ≤ ₹2,500/pair (5%)', group: 'Fashion & Apparel', gstRate: 0.05, hsn: '6402/6404' },

    // Jewellery & Accessories
    { label: 'Jewellery – Imitation, Beaded, Fashion Jewellery (3%)', group: 'Jewellery & Accessories', gstRate: 0.03, hsn: '7117' },
    { label: 'Jewellery – Traditional Lac / Shellac Bangles (0%)', group: 'Jewellery & Accessories', gstRate: 0.0, hsn: '7117' },
    { label: 'Jewellery – Precious Gold / Silver Ornaments (3%)', group: 'Jewellery & Accessories', gstRate: 0.03, hsn: '7113' },

    // Handicrafts & Home Decor
    { label: 'Handicrafts – Clay Diyas & Unglazed Pots (0%)', group: 'Handicrafts & Home Decor', gstRate: 0.0, hsn: '6912' },
    { label: 'Handicrafts – Terracotta & Painted Clay Art (5%)', group: 'Handicrafts & Home Decor', gstRate: 0.05, hsn: '6913' },
    { label: 'Handicrafts – Bamboo / Cane Baskets & Mats (5%)', group: 'Handicrafts & Home Decor', gstRate: 0.05, hsn: '4601/4602' },
    { label: 'Handicrafts – Wooden Carvings & Stone Art (5%)', group: 'Handicrafts & Home Decor', gstRate: 0.05, hsn: '4420/6802' },
    { label: 'Handicrafts – Paintings & Warli / Madhubani Wall Art (5%)', group: 'Handicrafts & Home Decor', gstRate: 0.05, hsn: '9701/7013' },
    { label: 'Home & Kitchen – Utensils, Spoons & Clay Cookware (5%)', group: 'Handicrafts & Home Decor', gstRate: 0.05, hsn: '7323/4419' },

    // Beauty, Personal Care & Puja
    { label: 'Beauty – Handmade Herbal Soaps (5%)', group: 'Beauty & Puja', gstRate: 0.05, hsn: '3401' },
    { label: 'Beauty – Ayurvedic & Herbal Hair Oils (5%)', group: 'Beauty & Puja', gstRate: 0.05, hsn: '3004/1515' },
    { label: 'Puja – Agarbatti, Dhoop Sticks, Hawan Samagri (5%)', group: 'Beauty & Puja', gstRate: 0.05, hsn: '3307' },
    { label: 'Agri – Organic Fertilizer & Bio-pesticides (5%)', group: 'Agriculture', gstRate: 0.05, hsn: '3101/3808' },

    // Custom
    { label: 'Custom GST Rate', group: 'Other', gstRate: 0.05 }
];

const Calculator: React.FC = () => {
    // Category & GST
    const [selectedCategoryIdx, setSelectedCategoryIdx] = useState<number>(0);
    const [customGstPercent, setCustomGstPercent] = useState<number>(5);

    // Section 1 Inputs - Starts empty / zero
    const [retailPrice, setRetailPrice] = useState<number | ''>('');
    const [profitMarginPercent, setProfitMarginPercent] = useState<number>(7);

    // Section 2 Inputs - Starts empty / zero
    const [packagingCost, setPackagingCost] = useState<number | ''>('');

    const [copied, setCopied] = useState<boolean>(false);

    // Determine GST Rate
    const isCustom = CATEGORY_OPTIONS[selectedCategoryIdx].label === 'Custom GST Rate';
    const effectiveGstRate = isCustom ? customGstPercent / 100 : CATEGORY_OPTIONS[selectedCategoryIdx].gstRate;

    const basePriceNum = typeof retailPrice === 'number' ? retailPrice : 0;
    const packagingNum = typeof packagingCost === 'number' ? packagingCost : 0;

    // ==========================================
    // SECTION 1 CALCULATIONS (from Excel Model)
    // ==========================================
    // C13 = C11 * C12
    const profitAmount = basePriceNum * (profitMarginPercent / 100);
    // C14 = C11 + C13
    const priceAfterProfit = basePriceNum + profitAmount;
    // C15 = C11 * 0.005 (0.5% TCS on Base Retail)
    const tcsAmount = basePriceNum * 0.005;
    // C16 = C11 * 0.001 (0.1% TDS on Base Retail)
    const tdsAmount = basePriceNum * 0.001;
    // C18 = C14 * C17 (GST on Price after Profit)
    const productGstAmount = priceAfterProfit * effectiveGstRate;
    // C19 = C14 + C18 + C15 + C16
    const productPricePostGst = priceAfterProfit + productGstAmount + tcsAmount + tdsAmount;

    // ==========================================
    // SECTION 2 CALCULATIONS (from Excel Model)
    // ==========================================
    // C22 = C19 * 0.05 (5% Buyer App Fee on Post-GST Price)
    const buyerAppFee = productPricePostGst * 0.05;
    // C23 = C19 * 0.05 (5% Seller App Fee on Post-GST Price)
    const sellerAppFee = productPricePostGst * 0.05;
    // C24 = (C22 + C23) * 0.18 (18% GST on App Fees)
    const gstOnAppFees = (buyerAppFee + sellerAppFee) * 0.18;
    // C25 = Packaging Cost (manual)
    const packagingAmount = packagingNum;
    // C26 = C19 * 0.02 (2% Misc buffer for returns/damages)
    const miscBuffer = productPricePostGst * 0.02;
    // C27 = SUM(C22:C26)
    const totalPlatformCharges = buyerAppFee + sellerAppFee + gstOnAppFees + packagingAmount + miscBuffer;
    // C28 = roundup(C19 + C27, 0)
    const finalMrp = basePriceNum > 0 ? Math.ceil(productPricePostGst + totalPlatformCharges) : 0;

    // Seller Net Payout = Production Cost + Desired Profit
    const sellerPayout = basePriceNum > 0 ? priceAfterProfit : 0;

    const resetFields = () => {
        setRetailPrice('');
        setProfitMarginPercent(7);
        setPackagingCost('');
        setSelectedCategoryIdx(0);
        toast.success('Reset all inputs');
    };

    const copyBreakdown = () => {
        if (basePriceNum <= 0) {
            toast.error('Please enter a base price first');
            return;
        }

        const text = `
=== ONDC PRICE CALCULATOR BREAKDOWN ===
Category: ${CATEGORY_OPTIONS[selectedCategoryIdx].label}

[SECTION 1: RETAIL & PRODUCT GST]
- Retail / Base Production Cost: ₹${basePriceNum.toFixed(2)}
- Profit Margin: ${profitMarginPercent}% (₹${profitAmount.toFixed(2)})
- Price after Profit: ₹${priceAfterProfit.toFixed(2)}
- TCS (0.5%): ₹${tcsAmount.toFixed(2)}
- TDS (0.1%): ₹${tdsAmount.toFixed(2)}
- Product GST (${(effectiveGstRate * 100).toFixed(1)}%): ₹${productGstAmount.toFixed(2)}
- Product Price Post GST: ₹${productPricePostGst.toFixed(2)}

[SECTION 2: PLATFORM & ADD-ONS]
- Buyer App Fee (5%): ₹${buyerAppFee.toFixed(2)}
- Seller App Fee (5%): ₹${sellerAppFee.toFixed(2)}
- GST on App Fees (18%): ₹${gstOnAppFees.toFixed(2)}
- Packaging Cost: ₹${packagingAmount.toFixed(2)}
- Miscellaneous Buffer (2%): ₹${miscBuffer.toFixed(2)}
- Total Platform Add-ons: ₹${totalPlatformCharges.toFixed(2)}

=======================================
RECOMMENDED ONDC LISTING MRP: ₹${finalMrp}
SELLER FINAL PAYOUT: ₹${sellerPayout.toFixed(2)}
=======================================
`.trim();

        navigator.clipboard.writeText(text);
        setCopied(true);
        toast.success('Breakdown copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="min-h-[calc(100vh-64px)] bg-slate-50 p-4 md:p-8">
            <div className="max-w-7xl mx-auto space-y-6">

                {/* Header */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200/80 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-emerald-600 text-white flex items-center justify-center shadow-md shadow-emerald-500/20">
                            <CalcIcon className="w-6 h-6" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="text-2xl font-bold text-slate-900 tracking-tight">ONDC Product Price Calculator</h1>
                                <span className="bg-emerald-100 text-emerald-800 text-xs font-semibold px-2.5 py-0.5 rounded-full border border-emerald-300">
                                    Section 1 & 2 Model
                                </span>
                            </div>
                            <p className="text-sm text-slate-500 mt-0.5">
                                Official ONDC Pricing: Base Retail → Profit → Product GST → Platform Add-ons → Final MRP
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={resetFields}
                            className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-600 transition"
                        >
                            <RotateCcw className="w-3.5 h-3.5" />
                            Reset Fields
                        </button>
                    </div>
                </div>

                {/* Main 2-Column Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

                    {/* Left Column: Form Controls (Sections 1 & 2) */}
                    <div className="lg:col-span-7 space-y-6">

                        {/* SECTION 1 CARD */}
                        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200/80 space-y-5">
                            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                                <div className="flex items-center gap-2">
                                    <Tag className="w-5 h-5 text-emerald-600" />
                                    <div>
                                        <h2 className="text-base font-semibold text-slate-800">Section 1 – Base Retail, Profit & Product GST</h2>
                                        <p className="text-xs text-slate-400">Production cost, seller profit margin, TCS, TDS & Product GST</p>
                                    </div>
                                </div>
                                <span className="text-xs font-bold px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">
                                    Section 1
                                </span>
                            </div>

                            {/* Category Selector */}
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                                    Product Category & GST Rate
                                </label>
                                <select
                                    value={selectedCategoryIdx}
                                    onChange={e => setSelectedCategoryIdx(Number(e.target.value))}
                                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 text-sm focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 outline-none transition cursor-pointer"
                                >
                                    {CATEGORY_OPTIONS.map((cat, idx) => (
                                        <option key={idx} value={idx}>
                                            {cat.label} {cat.hsn ? `(HSN: ${cat.hsn})` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {isCustom && (
                                <div className="p-3.5 bg-amber-50 rounded-xl border border-amber-200 flex items-center justify-between gap-4">
                                    <span className="text-xs font-medium text-amber-800">Enter Custom Product GST Rate:</span>
                                    <div className="flex items-center gap-1.5 w-32">
                                        <input
                                            type="number"
                                            min="0"
                                            max="28"
                                            step="0.5"
                                            value={customGstPercent}
                                            onChange={e => setCustomGstPercent(Number(e.target.value))}
                                            className="w-full px-2.5 py-1.5 bg-white border border-amber-300 rounded-lg text-sm text-center font-semibold text-amber-900 outline-none"
                                        />
                                        <span className="text-xs font-bold text-amber-800">%</span>
                                    </div>
                                </div>
                            )}

                            {/* Base Price */}
                            <div>
                                <div className="flex items-center justify-between mb-1.5">
                                    <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                                        Retail / Base Production Cost (₹)
                                    </label>
                                    <span className="text-xs text-slate-400">Before taxes & platform fees</span>
                                </div>
                                <div className="relative">
                                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-base">₹</span>
                                    <input
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={retailPrice}
                                        onChange={e => {
                                            const val = e.target.value;
                                            setRetailPrice(val === '' ? '' : Math.max(0, Number(val)));
                                        }}
                                        placeholder="Enter base production cost (₹)"
                                        className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-semibold text-base focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 outline-none transition"
                                    />
                                </div>
                            </div>

                            {/* Profit Margin */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                                        Desired Profit Margin %
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-emerald-600 font-semibold">+₹{profitAmount.toFixed(2)} profit</span>
                                        <span className="text-sm font-bold text-slate-800 bg-slate-100 px-2 py-0.5 rounded-md">
                                            {profitMarginPercent}%
                                        </span>
                                    </div>
                                </div>

                                <input
                                    type="range"
                                    min="0"
                                    max="50"
                                    step="0.5"
                                    value={profitMarginPercent}
                                    onChange={e => setProfitMarginPercent(Number(e.target.value))}
                                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                                />

                                <div className="flex flex-wrap gap-1.5 pt-1">
                                    {[0, 5, 10, 15, 20, 25, 30].map(pct => (
                                        <button
                                            key={pct}
                                            type="button"
                                            onClick={() => setProfitMarginPercent(pct)}
                                            className={`px-2.5 py-1 text-xs rounded-lg font-medium transition ${profitMarginPercent === pct
                                                    ? 'bg-emerald-600 text-white shadow-sm'
                                                    : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                                                }`}
                                        >
                                            {pct}%
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Section 1 Auto-Calculated Details */}
                            <div className="p-4 bg-slate-50/80 rounded-xl border border-slate-200/60 space-y-2 text-xs">
                                <div className="flex justify-between text-slate-600">
                                    <span>Price after Profit (Retail + Profit):</span>
                                    <span className="font-semibold text-slate-900">₹{priceAfterProfit.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-slate-500">
                                    <span className="flex items-center gap-1">
                                        TCS (0.5% on Base Price):
                                        <Info className="w-3 h-3 text-slate-400" />
                                    </span>
                                    <span>+ ₹{tcsAmount.toFixed(3)}</span>
                                </div>
                                <div className="flex justify-between text-slate-500">
                                    <span className="flex items-center gap-1">
                                        TDS (0.1% on Base Price):
                                        <Info className="w-3 h-3 text-slate-400" />
                                    </span>
                                    <span>+ ₹{tdsAmount.toFixed(3)}</span>
                                </div>
                                <div className="flex justify-between text-slate-500">
                                    <span>Product GST ({(effectiveGstRate * 100).toFixed(1)}% on Price after Profit):</span>
                                    <span>+ ₹{productGstAmount.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-slate-900 font-bold pt-2 border-t border-slate-200">
                                    <span>Product Price Post GST:</span>
                                    <span className="text-emerald-700">₹{productPricePostGst.toFixed(2)}</span>
                                </div>
                            </div>
                        </div>

                        {/* SECTION 2 CARD */}
                        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200/80 space-y-5">
                            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                                <div className="flex items-center gap-2">
                                    <Layers className="w-5 h-5 text-indigo-600" />
                                    <div>
                                        <h2 className="text-base font-semibold text-slate-800">Section 2 – Platform & Tax Add-on Charges</h2>
                                        <p className="text-xs text-slate-400">ONDC Network App Fees, GST on App Fees, Packaging & Buffer</p>
                                    </div>
                                </div>
                                <span className="text-xs font-bold px-2.5 py-1 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200">
                                    Section 2
                                </span>
                            </div>

                            {/* Packaging Cost */}
                            <div>
                                <div className="flex items-center justify-between mb-1.5">
                                    <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                                        Packaging Cost (₹ per unit)
                                    </label>
                                    <span className="text-xs text-slate-400">Box, pouch, label, tape, etc.</span>
                                </div>
                                <div className="relative">
                                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-base">₹</span>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.5"
                                        value={packagingCost}
                                        onChange={e => {
                                            const val = e.target.value;
                                            setPackagingCost(val === '' ? '' : Math.max(0, Number(val)));
                                        }}
                                        placeholder="Enter packaging cost (optional)"
                                        className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-semibold text-base focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-none transition"
                                    />
                                </div>
                            </div>

                            {/* Section 2 Breakdown Table */}
                            <div className="p-4 bg-indigo-50/40 rounded-xl border border-indigo-100 space-y-2 text-xs">
                                <div className="flex justify-between text-slate-600">
                                    <span>Buyer App Fee (5.0% on Post-GST Price):</span>
                                    <span className="font-semibold text-slate-800">₹{buyerAppFee.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-slate-600">
                                    <span>Seller App Fee (5.0% on Post-GST Price):</span>
                                    <span className="font-semibold text-slate-800">₹{sellerAppFee.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-slate-600">
                                    <span>GST on App Fees (18% on Buyer+Seller fees):</span>
                                    <span className="font-semibold text-slate-800">₹{gstOnAppFees.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-slate-600">
                                    <span>Packaging Cost:</span>
                                    <span className="font-semibold text-slate-800">₹{packagingAmount.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-slate-600">
                                    <span>Miscellaneous Buffer (2.0% for returns/damages):</span>
                                    <span className="font-semibold text-slate-800">₹{miscBuffer.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-indigo-900 font-bold pt-2 border-t border-indigo-200">
                                    <span>Total Platform & Add-on Charges:</span>
                                    <span className="text-indigo-700">₹{totalPlatformCharges.toFixed(2)}</span>
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* Right Column: Results & Analysis Card */}
                    <div className="lg:col-span-5 space-y-6">

                        {/* Hero Output Card */}
                        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-950 text-white rounded-3xl p-6 md:p-8 shadow-xl border border-slate-700/50 flex flex-col justify-between relative overflow-hidden">
                            {/* Decorative blur circle */}
                            <div className="absolute -top-16 -right-16 w-48 h-48 bg-emerald-500/15 rounded-full blur-3xl pointer-events-none" />

                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <span className="text-xs uppercase tracking-widest text-emerald-400 font-bold flex items-center gap-1.5">
                                        <Sparkles className="w-3.5 h-3.5" /> Final ONDC Pricing
                                    </span>
                                    <button
                                        onClick={copyBreakdown}
                                        className="flex items-center gap-1.5 text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg text-slate-200 transition backdrop-blur-sm"
                                    >
                                        {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                                        {copied ? 'Copied' : 'Copy'}
                                    </button>
                                </div>

                                <div className="space-y-1 mb-6">
                                    <p className="text-xs text-slate-400 uppercase tracking-wider">Recommended Listing Price (MRP)</p>
                                    <div className="text-5xl md:text-6xl font-extrabold text-white tracking-tight flex items-baseline gap-1">
                                        <span>₹{finalMrp}</span>
                                        <span className="text-xs text-emerald-400 font-normal">incl. all taxes & fees</span>
                                    </div>
                                    <p className="text-xs text-slate-400 pt-1">
                                        Rounded up to nearest ₹1 for official ONDC catalogue listing.
                                    </p>
                                </div>

                                {/* Seller Payout Card inside Hero */}
                                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/10 space-y-3 mb-6">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-emerald-300 font-medium flex items-center gap-1.5">
                                            <TrendingUp className="w-4 h-4" /> Seller Final Payout (Take Home)
                                        </span>
                                        <span className="text-xl font-bold text-white">
                                            ₹{sellerPayout.toFixed(2)}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 text-xs pt-2 border-t border-white/10 text-slate-300">
                                        <div>
                                            <span className="text-slate-400 block text-[10px] uppercase">Base Cost</span>
                                            <span className="font-semibold text-white">₹{basePriceNum.toFixed(2)}</span>
                                        </div>
                                        <div>
                                            <span className="text-slate-400 block text-[10px] uppercase">Net Profit ({profitMarginPercent}%)</span>
                                            <span className="font-semibold text-emerald-400">+₹{profitAmount.toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Visual Cost Distribution */}
                                {finalMrp > 0 && (
                                    <div className="space-y-2 mb-6">
                                        <div className="flex justify-between text-xs text-slate-400 font-medium">
                                            <span>Price Composition</span>
                                            <span>Total: ₹{finalMrp}</span>
                                        </div>
                                        <div className="h-2.5 w-full bg-slate-700/60 rounded-full flex overflow-hidden">
                                            <div
                                                style={{ width: `${Math.min(100, (basePriceNum / finalMrp) * 100)}%` }}
                                                className="bg-emerald-500 h-full"
                                                title="Base Retail"
                                            />
                                            <div
                                                style={{ width: `${Math.min(100, (profitAmount / finalMrp) * 100)}%` }}
                                                className="bg-emerald-300 h-full"
                                                title="Profit"
                                            />
                                            <div
                                                style={{ width: `${Math.min(100, ((productGstAmount + tcsAmount + tdsAmount + gstOnAppFees) / finalMrp) * 100)}%` }}
                                                className="bg-amber-400 h-full"
                                                title="Taxes & TDS/TCS"
                                            />
                                            <div
                                                style={{ width: `${Math.min(100, ((buyerAppFee + sellerAppFee + miscBuffer + packagingAmount) / finalMrp) * 100)}%` }}
                                                className="bg-indigo-400 h-full"
                                                title="Platform Fees & Buffer"
                                            />
                                        </div>
                                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-400 pt-1">
                                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Base ({((basePriceNum / finalMrp) * 100 || 0).toFixed(0)}%)</span>
                                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-300" /> Profit ({((profitAmount / finalMrp) * 100 || 0).toFixed(0)}%)</span>
                                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /> Taxes ({(((productGstAmount + tcsAmount + tdsAmount + gstOnAppFees) / finalMrp) * 100 || 0).toFixed(0)}%)</span>
                                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-indigo-400" /> ONDC Platform ({(((buyerAppFee + sellerAppFee + miscBuffer + packagingAmount) / finalMrp) * 100 || 0).toFixed(0)}%)</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Summary Checklist */}
                            <div className="pt-4 border-t border-slate-700/60 text-xs text-slate-400 space-y-1.5">
                                <div className="flex items-center gap-1.5 text-slate-300">
                                    <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                                    <span>TCS (0.5%) & TDS (0.1%) auto-deducted before payout</span>
                                </div>
                                <div className="flex items-center gap-1.5 text-slate-300">
                                    <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                                    <span>Includes Buyer (5%) & Seller (5%) ONDC App Fees + 18% GST</span>
                                </div>
                            </div>
                        </div>

                        {/* Full Comparison Table Card */}
                        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200/80 space-y-4">
                            <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">
                                Detailed Breakdown Table
                            </h3>

                            <div className="overflow-x-auto">
                                <table className="w-full text-xs text-left text-slate-600">
                                    <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider border-b border-slate-200">
                                        <tr>
                                            <th className="py-2.5 px-3 font-semibold">Component</th>
                                            <th className="py-2.5 px-3 font-semibold text-right">Amount (₹)</th>
                                            <th className="py-2.5 px-3 font-semibold text-right">Rate / Basis</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 font-medium">
                                        <tr>
                                            <td className="py-2 px-3 text-slate-900">Retail / Base Price</td>
                                            <td className="py-2 px-3 text-right font-bold text-slate-900">₹{basePriceNum.toFixed(2)}</td>
                                            <td className="py-2 px-3 text-right text-slate-400">Baseline</td>
                                        </tr>
                                        <tr>
                                            <td className="py-2 px-3 text-emerald-700">Profit Margin</td>
                                            <td className="py-2 px-3 text-right font-bold text-emerald-700">+ ₹{profitAmount.toFixed(2)}</td>
                                            <td className="py-2 px-3 text-right text-slate-400">{profitMarginPercent}% of Base</td>
                                        </tr>
                                        <tr>
                                            <td className="py-2 px-3 text-slate-700 font-semibold">Price after Profit</td>
                                            <td className="py-2 px-3 text-right font-bold text-slate-800">₹{priceAfterProfit.toFixed(2)}</td>
                                            <td className="py-2 px-3 text-right text-slate-400">Retail + Profit</td>
                                        </tr>
                                        <tr>
                                            <td className="py-2 px-3">TCS (Tax Collected at Source)</td>
                                            <td className="py-2 px-3 text-right font-semibold">₹{tcsAmount.toFixed(3)}</td>
                                            <td className="py-2 px-3 text-right text-slate-400">0.5% of Base</td>
                                        </tr>
                                        <tr>
                                            <td className="py-2 px-3">TDS (Tax Deducted at Source)</td>
                                            <td className="py-2 px-3 text-right font-semibold">₹{tdsAmount.toFixed(3)}</td>
                                            <td className="py-2 px-3 text-right text-slate-400">0.1% of Base</td>
                                        </tr>
                                        <tr>
                                            <td className="py-2 px-3">Product GST</td>
                                            <td className="py-2 px-3 text-right font-semibold">₹{productGstAmount.toFixed(2)}</td>
                                            <td className="py-2 px-3 text-right text-slate-400">{(effectiveGstRate * 100).toFixed(1)}% of (Retail+Profit)</td>
                                        </tr>
                                        <tr className="bg-slate-50 font-bold text-slate-900">
                                            <td className="py-2 px-3">Product Price Post GST</td>
                                            <td className="py-2 px-3 text-right text-emerald-700">₹{productPricePostGst.toFixed(2)}</td>
                                            <td className="py-2 px-3 text-right text-slate-400">Post-GST Base</td>
                                        </tr>
                                        <tr>
                                            <td className="py-2 px-3">Buyer App Fee</td>
                                            <td className="py-2 px-3 text-right font-semibold">₹{buyerAppFee.toFixed(2)}</td>
                                            <td className="py-2 px-3 text-right text-slate-400">5.0% on Post-GST</td>
                                        </tr>
                                        <tr>
                                            <td className="py-2 px-3">Seller App Fee</td>
                                            <td className="py-2 px-3 text-right font-semibold">₹{sellerAppFee.toFixed(2)}</td>
                                            <td className="py-2 px-3 text-right text-slate-400">5.0% on Post-GST</td>
                                        </tr>
                                        <tr>
                                            <td className="py-2 px-3">GST on App Fees</td>
                                            <td className="py-2 px-3 text-right font-semibold">₹{gstOnAppFees.toFixed(2)}</td>
                                            <td className="py-2 px-3 text-right text-slate-400">18.0% on App Fees</td>
                                        </tr>
                                        <tr>
                                            <td className="py-2 px-3">Packaging Cost</td>
                                            <td className="py-2 px-3 text-right font-semibold">₹{packagingAmount.toFixed(2)}</td>
                                            <td className="py-2 px-3 text-right text-slate-400">Per unit</td>
                                        </tr>
                                        <tr>
                                            <td className="py-2 px-3">Miscellaneous Buffer</td>
                                            <td className="py-2 px-3 text-right font-semibold">₹{miscBuffer.toFixed(2)}</td>
                                            <td className="py-2 px-3 text-right text-slate-400">2.0% on Post-GST</td>
                                        </tr>
                                        <tr className="bg-emerald-50 font-extrabold text-emerald-900 text-sm">
                                            <td className="py-2.5 px-3">ONDC Listing MRP</td>
                                            <td className="py-2.5 px-3 text-right text-emerald-700">₹{finalMrp}</td>
                                            <td className="py-2.5 px-3 text-right text-emerald-600">Rounded up to nearest ₹1</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                    </div>

                </div>

            </div>
        </div>
    );
};

export default Calculator;
